import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

/** Shared catalog; each chart filters by available axis / stack / metric keys. */
export const REGION_CHART_PRESETS = [
  {
    id: 'region-segment-count',
    label: 'Región · Segmento · Cantidad',
    axisKey: 'region',
    stackKey: 'segment',
    metricKey: 'count',
  },
  {
    id: 'region-status-count',
    label: 'Región · Estado · Cantidad',
    axisKey: 'region',
    stackKey: 'status',
    metricKey: 'count',
  },
  {
    id: 'region-status-gallons',
    label: 'Región · Estado · Galones T-COAT',
    axisKey: 'region',
    stackKey: 'status',
    metricKey: 'gallons',
  },
  {
    id: 'gallon-status-gallons',
    label: 'Precio/galón · Estado · Galones T-COAT',
    axisKey: 'gallonPrice',
    stackKey: 'status',
    metricKey: 'gallons',
  },
  {
    id: 'gallon-segment-gallons',
    label: 'Precio/galón · Segmento · Galones T-COAT',
    axisKey: 'gallonPrice',
    stackKey: 'segment',
    metricKey: 'gallons',
  },
  {
    id: 'amount-status-amount',
    label: 'Monto · Estado · Montos',
    axisKey: 'amount',
    stackKey: 'status',
    metricKey: 'amount',
  },
  {
    id: 'amount-segment-amount',
    label: 'Monto · Segmento · Montos',
    axisKey: 'amount',
    stackKey: 'segment',
    metricKey: 'amount',
  },
  {
    id: 'region-status-saleRate',
    label: 'Región · Estado · Tasa de venta',
    axisKey: 'region',
    stackKey: 'status',
    metricKey: 'saleRate',
  },
];

export const MONTHLY_CHART_PRESETS = [
  {
    id: 'month-status-count',
    label: 'Mes · Estado · Cantidad',
    axisKey: 'month',
    stackKey: 'status',
    metricKey: 'count',
  },
  {
    id: 'month-segment-count',
    label: 'Mes · Segmento · Cantidad',
    axisKey: 'month',
    stackKey: 'segment',
    metricKey: 'count',
  },
  {
    id: 'month-status-gallons',
    label: 'Mes · Estado · Galones T-COAT',
    axisKey: 'month',
    stackKey: 'status',
    metricKey: 'gallons',
  },
  {
    id: 'month-status-amount',
    label: 'Mes · Estado · Montos',
    axisKey: 'month',
    stackKey: 'status',
    metricKey: 'amount',
  },
  {
    id: 'gallon-status-gallons',
    label: 'Precio/galón · Estado · Galones T-COAT',
    axisKey: 'gallonPrice',
    stackKey: 'status',
    metricKey: 'gallons',
  },
  {
    id: 'gallon-segment-gallons',
    label: 'Precio/galón · Segmento · Galones T-COAT',
    axisKey: 'gallonPrice',
    stackKey: 'segment',
    metricKey: 'gallons',
  },
  {
    id: 'amount-status-amount',
    label: 'Monto · Estado · Montos',
    axisKey: 'amount',
    stackKey: 'status',
    metricKey: 'amount',
  },
  {
    id: 'month-productLine-amount',
    label: 'Mes · Línea · Montos',
    axisKey: 'month',
    stackKey: 'productLine',
    metricKey: 'amount',
  },
];

export const filterChartPresets = (presets = [], { axisModes = [], dimensions = [], metrics = [] } = {}) => {
  const axisKeys = new Set((axisModes || []).map((mode) => mode.key));
  const stackKeys = new Set((dimensions || []).map((dimension) => dimension.key));
  const metricKeys = new Set((metrics || []).map((metric) => metric.key));
  return (presets || []).filter(
    (preset) =>
      axisKeys.has(preset.axisKey) &&
      stackKeys.has(preset.stackKey) &&
      metricKeys.has(preset.metricKey)
  );
};

/**
 * Top-right chevron menu with predefined chart configurations.
 */
const ChartPresetsMenu = ({
  presets = [],
  axisKey,
  stackKey,
  metricKey,
  onApply,
}) => {
  const ui = useUIColors();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const available = useMemo(() => (Array.isArray(presets) ? presets : []), [presets]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!available.length) return null;

  const border = ui.border;
  const panel = ui.panel;
  const text = ui.text;
  const muted = ui.muted;
  const headerBg = ui.header;
  const activeBg = `color-mix(in srgb, ${ui.interactive} 14%, ${panel})`;

  const isActive = (preset) =>
    preset.axisKey === axisKey &&
    preset.stackKey === stackKey &&
    preset.metricKey === metricKey;

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label="Gráficos predeterminados"
        title="Gráficos predeterminados"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          borderRadius: '7px',
          border: `1px solid ${border}`,
          background: open ? activeBg : panel,
          color: text,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ChevronDown size={16} strokeWidth={2.25} />
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: '260px',
            maxWidth: '340px',
            maxHeight: '360px',
            overflowY: 'auto',
            background: panel,
            border: `1px solid ${border}`,
            borderRadius: '8px',
            boxShadow: ui.shadowMd || '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: '7px 10px',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: muted,
              background: headerBg,
              borderBottom: `1px solid ${border}`,
            }}
          >
            Gráficos predeterminados
          </div>
          {available.map((preset) => {
            const selected = isActive(preset);
            return (
              <button
                key={preset.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onApply?.(preset);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  width: '100%',
                  border: 'none',
                  borderBottom: `1px solid color-mix(in srgb, ${border} 70%, transparent)`,
                  background: selected ? activeBg : 'transparent',
                  color: text,
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '8px 10px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>{preset.label}</span>
                {selected ? <Check size={14} color={ui.interactive} /> : <span style={{ width: 14 }} />}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ChartPresetsMenu;
